import PropTypes from "prop-types"
import React from "react"
import styled from "styled-components"
import { getLanguage, getTranslation } from "../../utils"
import GatsbyImage from "../common/gatsby-image"
import BackLink from "../common/back-link"
import Container from "../common/container"
import ProductGallery from "./product-gallery"
import ProductData from "./product-data"
import Banner from "../common/banner"
import Line from "../common/line"
import ItemsCarousel from "../common/items-carousel"
import RecipesCarousel from "../common/recipes-carousel"

const StyledProduct = styled.div`
  margin-bottom: 2rem;

  @media (min-width: 1024px) {
    margin-bottom: 5rem;
  }

  .is-video {
    padding-top: 2rem;

    + div:not(.banner) {
      margin-top: 0 !important;
    }

    + .banner {
      margin-top: 2rem;

      @media (min-width: 1024px) {
        margin-top: 4rem;
      }
    }
  }
`

const StyledProductContent = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 3rem;

  @media (min-width: 1024px) {
    flex-direction: row;
    margin-bottom: 5rem;
  }
`

const ProductFeatures = styled.ul`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0;

  @media (min-width: 767px) {
    flex-direction: row;
    flex-wrap: wrap;
  }
`

const ProductFeature = styled.li`
  display: flex;
  flex-direction: column;
  margin-bottom: 3rem;

  @media (min-width: 767px) {
    width: 50%;
    padding: 0 3rem;
  }

  @media (min-width: 1024px) {
    width: 25%;

    &:first-child {
      margin-left: -3rem;
    }

    &:last-child {
      margin-right: -3rem;
    }
  }

  @media (min-width: 1024px) {
    > div {
      max-height: 21rem;
    }
  }

  .gatsby-image-wrapper {
    max-width: 15rem;
    padding-bottom: 66.666%;
    margin: 0 auto;

    @media (min-width: 1024px) {
      max-width: 100%;
    }

    img {
      object-fit: contain !important;
    }
  }

  p {
    font-family: ${props => props.theme.crockpot.secondFont};
    font-size: 1.5rem;
    line-height: 1.7rem;
    color: ${props => props.theme.crockpot.textSecond};
    text-transform: uppercase;
    text-align: center;
    padding-top: 2rem;

    @media (min-width: 1024px) {
      font-size: 2.5rem;
      line-height: 3rem;
    }
  }
`

const ProductContent = ({
  product = null,
  showRatings = false,
  categorySlug,
}) => {
  if (!product) return null

  const categoryLink = `${getTranslation("category")}`

  let extraTranslation = ""

  const locale = getLanguage()

  let slowFrench

  if (locale === "es") {
    extraTranslation = " las "
  } else if (locale === "fr") {
    extraTranslation = " les "

    if (product.category === "Slow Cookers") {
      slowFrench = true
    }
  }

  const backTextVariation =
    extraTranslation === " las " || slowFrench || extraTranslation === " "

  return (
    <StyledProduct>
      <Container>
        <BackLink
          link={`${categoryLink}/${categorySlug}`}
          title={`${getTranslation(
            backTextVariation ? "all-2" : "all",
          )} ${extraTranslation} ${getTranslation(
            product.category.replace(" ", "-").toLowerCase(),
          )}`}
        />

        <StyledProductContent>
          <ProductGallery images={product?.productImages} />
          <ProductData product={product} showRatings={showRatings} />
        </StyledProductContent>

        {product.features && (
          <ProductFeatures>
            {product.features.map((feature, index) => (
              <ProductFeature key={index}>
                {feature.image && (
                  <GatsbyImage image={feature.image.gatsbyImageData} />
                )}
                <p>{feature.title}</p>
              </ProductFeature>
            ))}
          </ProductFeatures>
        )}

        {product.relatedContent && (
          <Banner
            videoFile={product.relatedContent?.url}
            image={product.relatedContent?.gatsbyImageData}
          />
        )}

        {product.youtubeLink && (
          <Banner videoFile={product.youtubeLink} isYoutube={true} />
        )}

        {product.secondaryRelatedContent && (
          <Banner
            image={product.secondaryRelatedContent?.gatsbyImageData}
            link={product.secondaryRelatedContentUrl}
          />
        )}
      </Container>

      {product.relatedProducts?.length > 0 && (
        <>
          <Line />
          <ItemsCarousel
            items={product.relatedProducts}
            title={getTranslation("more-products")}
          />
        </>
      )}

      {showRatings && product.bvProductId && (
        <div data-bv-show="reviews" data-bv-product-id={product.bvProductId} />
      )}

      {product.recommendedRecipes?.length > 0 && (
        <>
          <Line />
          <RecipesCarousel
            items={product.recommendedRecipes}
            title={getTranslation("recommended-recipes")}
            hasBottomMargin={false}
          />
        </>
      )}
    </StyledProduct>
  )
}

ProductContent.propTypes = {
  product: PropTypes.object,
  showRatings: PropTypes.bool,
}

export default ProductContent
